import { css, useTheme } from '@emotion/react'
import Image from './Image'
import clsx from 'clsx'
import { MediaQuery } from './Utility'
import Link from 'next/link'
import { IoLogoYoutube } from 'react-icons/io'
import { IoMusicalNotes } from 'react-icons/io5'

function imageSizeSwitch(size) {
    switch (size) {
        case 'narrow':
            return '30%'
        case 'wide':
            return '100%'
        case 'normal':
            return '60%'
        default:
            return '60%'
    }
}
function switchBorderStyle(style, theme) {
    switch (style) {
        case 'rounded':
            return theme.borderRadius?.default
        case 'square':
            return '0'
    }
}
function paddingSwitch(space, theme) {
    const spacing = theme.spacing
    switch (space) {
        case 'xs':
            return spacing?.one
        case 'sm':
            return spacing?.two
        case 'md':
            return spacing?.four
        case 'lg':
            return spacing?.six
        case 'xl':
            return spacing?.eight
        case 'xxl':
            return spacing?.ten
        default:
            return spacing?.six
    }
}

function Card({
    type = 'default',
    align = 'start',
    header,
    onMouseOver,
    onMouseOut,
    href,
    mediaType,
    videoSrc,
    imgSrc,
    imgAlt = 'alt',
    imgWidth = '1200px',
    imgHeight = '600px',
    borderStyle = 'rounded',
    color,
    height = 'auto',
    subtitle,
    imgLayout,
    horizentalImageSize,
    title,
    imgFullRound = false,
    children,
    bordered,
    className,
    shadow,
    spacing = 'lg',
    footer,
    style,
}) {
    const theme = useTheme()
    const mainStyle = `
        background-color: ${color || theme.colors?.white};
        ${shadow && `box-shadow: ${theme.boxShadow?.default}`};
        border-radius: ${switchBorderStyle(borderStyle, theme)};
        ${bordered && 'border: 1px solid #dbdbdb'};
        height: ${height};
        ${style};
    `
    const mediaTypeStyle = css`
        background-color: #ffffff;
        position: absolute;
        z-index: 9;
        display: flex;
        padding: 0.25rem 0.5rem;
        ${(type === 'default') && 'top: -3rem;'}
        ${(type === 'horizental') && 'top: 1rem;'}
        align-items: center;
        gap: 0.5rem;
        ${(type === 'default') && 'right: 0;'}
    `
    const videoTag =
        <span css={mediaTypeStyle}>
            <IoLogoYoutube />
            <span>ویدیو</span>
        </span>

    const audioTag =
        <span css={mediaTypeStyle}>
            <IoMusicalNotes />
            <span>صوتی</span>
        </span>

    let inner = null
    if (type === 'default') {
        inner = (
            <div
                className={clsx('card default-card vertical-card relative', className)}
                css={css`
                    display: flex;
                    flex-direction: column;
                    ${mainStyle}
                `}
                onMouseOver={onMouseOver}
                onMouseOut={onMouseOut}
            >
                {header && (
                    <div
                        className="py-2 header"
                        css={css`
                            display: flex;
                            align-items: center;
                            padding: ${paddingSwitch(spacing, theme)}
                                ${paddingSwitch(spacing, theme)} 0
                                ${paddingSwitch(spacing, theme)};
                        `}
                    >
                        {header}
                    </div>
                )}
                {imgSrc && (


                    <Image
                        noStyle
                        priority={true}
                        src={imgSrc}
                        alt={imgAlt}
                        width={imgWidth}
                        height={imgHeight}
                        layout={imgLayout || 'responsive'}
                        objectFit="cover"
                        css={css`
                            object-fit: cover;
                            ${imgFullRound &&
                            (header
                                ? 'border-radius: 0;'
                                : /* eslint-disable indent */
                                `border-radius: ${switchBorderStyle(
                                    borderStyle,
                                    theme
                                )};`)};
                            ${!imgFullRound &&
                            (header
                                ? 'border-top-left-radius: 0; border-top-right-radius: 0;'
                                : /* eslint-disable indent */
                                `border-top-left-radius:${switchBorderStyle(
                                    borderStyle,
                                    theme
                                )}; 
                              border-top-right-radius:${switchBorderStyle(
                                    borderStyle,
                                    theme
                                )};`)};

                            /* eslint-enable indent */
                        `}
                    />

                )}
                {videoSrc && (
                    <video
                        muted
                        loop
                        controls
                        preload="metadata"
                        width={imgWidth}
                        height={imgHeight}
                        css={css`
                            object-fit: cover;
                            ${imgFullRound &&
                            (header
                                ? 'border-radius: 0;'
                                : /* eslint-disable indent */
                                `border-radius: ${switchBorderStyle(
                                    borderStyle,
                                    theme
                                )};`)};
                            ${!imgFullRound &&
                            (header
                                ? 'border-top-left-radius: 0; border-top-right-radius: 0;'
                                : /* eslint-disable indent */
                                `border-top-left-radius:${switchBorderStyle(
                                    borderStyle,
                                    theme
                                )}; 
                          border-top-right-radius:${switchBorderStyle(
                                    borderStyle,
                                    theme
                                )};`)};

                            /* eslint-enable indent */
                        `}
                    >
                        <source src={`${videoSrc}#t=0.5`} />
                    </video>
                )}

                <div
                    className="md:container card-body relative"
                    css={css`
                        padding: ${paddingSwitch(spacing, theme)};
                        flex: 1;
                        ${'' /* height: 100%; */}
                    `}
                >
                    {mediaType === 'video' && videoTag}
                    {mediaType === 'audio' && audioTag}
                    {subtitle && (
                        <div className="card-subtitle">{subtitle}</div>
                    )}
                    {title && (
                        <div className="card-title mt-2 mb-4">{title}</div>
                    )}
                    {children && <div className="card-content">{children}</div>}
                </div>
                <div className="w-full">
                    {footer && (
                        <div
                            className="card-footer"
                            css={css`
                                ${'' /* margin-top: 10px; */}
                                display: flex;
                                padding: 0 ${paddingSwitch(spacing, theme)};
                            `}
                        >
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        )
    } else if (type === 'horizental') {
        inner = (
            <div
                className={clsx('card horizental-card', className)}
                css={css`
                    display: flex;
                    height: 100%;
                    ${align === 'start' && 'flex-direction: row'};
                    ${align === 'end' && 'flex-direction: row-reverse'};
                    ${mainStyle}
                `}
                onMouseOver={onMouseOver}
                onMouseOut={onMouseOut}
            >
                {(imgSrc || videoSrc) && (
                    <div
                        className="card-image"
                        css={css`
                            width: ${imageSizeSwitch(horizentalImageSize)};
                            position: relative;
                            ${MediaQuery('sm', 'max')} {
                                width: 70%;
                            }
                        `}
                    >
                        {mediaType === 'video' && videoTag}
                        {mediaType === 'audio' && audioTag}
                        {imgSrc && (
                            <Image
                                noStyle
                                priority={true}
                                src={imgSrc}
                                width={imgWidth}
                                height={imgWidth}
                                alt={imgAlt}
                                layout="fill"
                                objectFit="cover"
                                css={css`
                                    object-fit: cover;
                                    ${!imgFullRound &&
                                    align === 'start' &&
                                    `border-top-right-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};
                border-bottom-right-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};`};
                                    ${!imgFullRound &&
                                    align === 'end' &&
                                    `border-top-left-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};
                border-bottom-left-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};`}
                                    ${imgFullRound &&
                                    `border-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};`};
                                `}
                            />
                        )}
                        {videoSrc && (
                            <video
                                muted
                                loop
                                controls
                                preload="metadata"
                                width={imgWidth}
                                height={imgWidth}
                                css={css`
                                    object-fit: cover;
                                    ${!imgFullRound &&
                                    align === 'start' &&
                                    `border-top-right-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};
        border-bottom-right-radius: ${switchBorderStyle(borderStyle, theme)};`};
                                    ${!imgFullRound &&
                                    align === 'end' &&
                                    `border-top-left-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};
        border-bottom-left-radius: ${switchBorderStyle(borderStyle, theme)};`}
                                    ${imgFullRound &&
                                    `border-radius: ${switchBorderStyle(
                                        borderStyle,
                                        theme
                                    )};`};
                                `}
                            >
                                <source src={`${videoSrc}#t=0.5`} />
                            </video>
                        )}
                    </div>
                )}

                <div
                    className="md:container w-full card-body"
                    css={css`
                        padding: ${paddingSwitch(spacing, theme)};
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                    `}
                >
                    {subtitle && (
                        <div className="card-subtitle">{subtitle}</div>
                    )}
                    {title && (
                        <div className="card-title pt-1 mb-4">{title}</div>
                    )}
                    {children && (
                        <div
                            className="card-content"
                            css={css`
                                flex: 1;
                                align-self: start;
                            `}
                        >
                            {children}
                        </div>
                    )}
                    {footer && (
                        <div
                            className="card-footer"
                            css={css`
                                margin-top: 10px;
                                display: flex;
                            `}
                        >
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    if (href)
        return (
            <Link href={href}>
                <a>{inner}</a>
            </Link>
        )

    return inner
}

export default Card
